<template>
  <div class="validated-basket">
    <b-alert variant="danger"
             :show="bookingValidations && !bookingValidations.valid">
      <h2 class="alert-heading">
        Error:
      </h2>
      <p class="alert-msg">
        Please check the highlighted bookings below.
      </p>
    </b-alert>

    <b-alert variant="danger"
             :show="addressValidations && !addressValidations.valid">
      <h2 class="alert-heading">
        Error:
      </h2>
      <p class="alert-msg">
        Please supply your address for the highlighted products below.
      </p>
    </b-alert>

    <b-alert variant="danger"
             :show="invalidVouchers && Object.keys( invalidVouchers ).length > 0">
      <h2 class="alert-heading">
        Error:
      </h2>
      <p class="alert-msg">
        The following voucher codes have expired or are not valid for this order:
      </p>
      <ul class="alert-list">
        <li v-for="(invalidVoucher, index) in invalidVouchers"
            :key="index"
            class="alert-item">
          {{ invalidVouchers }}
        </li>
      </ul>
    </b-alert>

    <b-alert variant="danger"
             :show="unmetPreRequesiteProducts && unmetPreRequesiteProducts.length > 0">
      <h2 class="alert-heading">
        Error:
      </h2>
      <p class="alert-msg">
        You are not allowed to complete this basket due to the following products,
        these products need to have either been purchased
        in the past or included in this basket:
      </p>
      <ul class="alert-list">
        <li v-for="(product, index) in unmetPreRequesiteProducts"
            :key="index"
            class="alert-item">
          {{ product }}
        </li>
      </ul>
    </b-alert>

    <slot name="basket"
          :model="model">
      <styled-basket :web-account-id="wid"
                     :booking-validations="bookingValidations"
                     :address-validations="addressValidations"
                     :search-type-id="searchTypeId"
                     :refresh="refreshing"
                     :logged-in="loggedIn"
                     :enable-vouchers="enableVouchers"
                     :currency="currency"
                     :locale="locale"
                     @refresh-bookings="validateBookings( true )"
                     @refresh-addresses="validateAddresses( true )"
                     @refreshed="refreshing = false">
        <template #submit>
          <slot
            :valid="valid"
            name="submit">
          </slot>
        </template>
      </styled-basket>
    </slot>
  </div>
</template>

<script>
import StyledBasket from "~/Components/Basket/StyledBasket.vue";
// Ignoring booking validations for now
// import { validateBookings } from "~/Scripts/services/eventsService";
import { id } from "~/Scripts/services/webAccountService";
import {
  validateOrder,
  validateAddresses,
  basketHasOrderLines,
} from "~/Scripts/services/basketStepService";

export default {
  name: "ValidatedBasket",
  components: {
    StyledBasket,
  },
  props: {
    valWebAccountId: {
      type: Boolean,
      default: true,
    },
    valBookings: {
      type: Boolean,
      default: true,
    },
    valAddresses: {
      type: Boolean,
      default: true,
    },
    valOrder: {
      type: Boolean,
      default: true,
    },
    valOrderLines: {
      type: Boolean,
      default: true,
    },
    refresh: {
      type: Boolean,
      default: false,
    },
    searchTypeId: {
      type: String,
      default: null,
    },
    webAccountId: {
      type: String,
      default: null,
    },
    enableVouchers: {
      type: Boolean,
      default: null,
    },
    loggedIn: {
      type: Boolean,
      default: false,
    },
    currency: {
      type: String,
      default: "GBP",
    },
    locale: {
      type: String,
      default: "en-GB",
    },
  },
  data () {
    return {
      intlRefreshing: false,
      loaded: false,
      orderValid: false,
      hasOrderLines: false,
      invalidVouchers: true,
      unmetPreRequesiteProducts: null,
      bookingValidations: null,
      addressValidations: null,
      interval: null,
      intlWebAccountId: null,
    };
  },
  computed: {
    wid: {
      get () {
        return this.webAccountId ?? this.intlWebAccountId;
      },
      set ( val ) {
        this.intlWebAccountId = val;
        this.$emit( "update:web-account-id", val );
      },
    },
    refreshing: {
      get () {
        return this.refresh || this.intlRefreshing;
      },
      set ( val ) {
        this.intlRefreshing = val;
        this.$emit( "update:refresh", val );
      },
    },
    validationMethods () {
      let methods = [];
      methods = this.valWebAccountId ? [...methods, this.cacheWebAccountId] : methods;
      methods = this.valBookings ? [...methods, this.validateBookings] : methods;
      methods = this.valAddresses ? [...methods, this.validateAddresses] : methods;
      methods = this.valOrder ? [...methods, this.validateOrder] : methods;
      methods = this.valOrderLines ? [...methods, this.basketHasOrderLines] : methods;
      return methods;
    },
    valid () {
      let result = true;

      if ( this.valOrder && !this.orderValid ) {
        result = false;
      }

      // Ignoring booking validations for now
      // if ( result && this.valBookings && !this.bookingValidations?.valid ) {
      //   result = false;
      // }

      if ( result && this.valAddresses && !this.addressValidations?.valid ) {
        result = false;
      }

      if ( result && this.valOrderLines && !this.hasOrderLines ) {
        result = false;
      }

      return result;
    },
    model () {
      const data = {
        state: this.state,
        setters: this.setters,
        functions: this.functions,
      };

      const ready = data.state && data.setters && data.functions;

      return {
        ...data,
        ready,
      };
    },
    state () {
      return {
        valid: this.valid,
        loaded: this.loaded,
        refreshing: this.refreshing,
        orderValid: this.orderValid,
        hasOrderLines: this.hasOrderLines,
        invalidVouchers: this.invalidVouchers,
        unmetPreRequesiteProducts: this.unmetPreRequesiteProducts,
        bookingValidations: this.bookingValidations,
        addressValidations: this.addressValidations,
        interval: this.interval,
      };
    },
    setters () {
      return {
        refreshing: this.setRefreshing,
      };
    },
    functions () {
      return {
        validateBookings: this.validateBookings,
        validateAddresses: this.validateAddresses,
      };
    },
  },
  created () {
    this.onValidateTick();
    this.interval = window.setInterval( () => {
      this.onValidateTick();
    }, 30000 );
  },
  beforeDestroy () {
    window.clearInterval( this.interval );
    this.interval = null;
  },
  methods: {
    onValidateTick () {
      Promise.all( this.validationMethods.map( m => m() ) )
        .then( () => {
          this.loaded = true;
        } )
        .catch( () => {
          this.orderValid = false;
          throw new Error( "Something went wrong whilst validating..." );
        } );
    },

    setRefreshing ( val ) {
      this.refreshing = val;
    },

    basketHasOrderLines ( isUpdate ) {
      this.loaded = false;

      return basketHasOrderLines()
        .then( ( response ) => {
          this.hasOrderLines = response.data;
        } )
        .finally( () => {
          if ( isUpdate ) {
            this.loaded = true;
          }
        } );
    },

    cacheWebAccountId ( isUpdate ) {
      this.loaded = false;

      return id()
        .then( ( response ) => {
          this.wid = response.data;
        } )
        .finally( () => {
          if ( isUpdate ) {
            this.loaded = true;
          }
        } );
    },

    validateOrder ( isUpdate ) {
      this.loaded = false;

      return validateOrder()
        .then( ( response ) => {
          this.orderValid = response.data.isValid;
          this.invalidVouchers = response.data.invalidVouchers;
          this.unmetPreRequesiteProducts = response.data.unmetPreRequesiteProducts;
        } )
        .finally( () => {
          if ( isUpdate ) {
            this.loaded = true;
          }
        } );
    },

    // Ignoring booking validations for now,
    // this was causing errors.
    validateBookings () {
      // this.loaded = false;
      // return validateBookings()
      //   .then( () => {
      //      this.bookingValidations = response.data;
      //   } )
      //   .finally( () => {
      //     if ( isUpdate ) {
      //       this.loaded = true;
      //     }
      //   } );
    },
    validateAddresses ( isUpdate ) {
      this.loaded = false;

      return validateAddresses()
        .then( ( response ) => {
          this.addressValidations = response.data;
        } )
        .finally( () => {
          if ( isUpdate ) {
            this.loaded = true;
          }
        } );
    },
  },
};
</script>

<style lang="less" scoped>

</style>
